import { FileTextOutlined, ReadOutlined, TeamOutlined } from '@ant-design/icons'
import { Avatar, Image, List, Popover } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { GroupAvatar, UserHoverCard } from '@/components'
import { GroupPrivateSetting, GroupWithExtrasFragment } from '@/generated/graphql'
import { Follow } from '@/modules/follow'
import { routes } from '@/routes'

import s from './group-card.module.less'

interface GroupCardProps {
  group: GroupWithExtrasFragment
}

export const DiscoverGroupCard: React.FC<GroupCardProps> = ({ group: g }) => {
  const { t } = useTranslation()

  const image = (
    <div className={cls(s.image, g.privateSetting == GroupPrivateSetting.Normal ? s.normalGroup : s.closedGroup)}>
      <Image preview={false} />
    </div>
  )

  const avatar = (
    <Avatar className={s.avatar}>
      <span style={{ fontSize: 30 }}>
        <GroupAvatar groupCatId={g.category?.groupCategoryId} />
      </span>
    </Avatar>
  )

  const title = (
    <Popover content={g.name}>
      <div className={s.title}>
        <Link to={routes.groups.page(g.groupId)}>{g.name}</Link>
      </div>
    </Popover>
  )

  const subtitle = (
    <div className={s.subtitle}>
      <span>
        <TeamOutlined style={{ marginRight: 4 }} />
        {g.followers!.totalCount} {t('groups.card.followers')}
      </span>
      <span>
        <FileTextOutlined style={{ marginRight: 4 }} />
        {g.posts!.totalCount} {t('groups.card.post')}
      </span>
    </div>
  )

  const description = (
    <div className={s.description}>
      <Popover
        content={
          <div style={{ width: 168, wordBreak: 'break-word', textAlign: 'center' }}>
            {g.description ? g.description : t('group.noInfo')}
          </div>
        }
      >
        <ReadOutlined style={{ marginRight: 4 }} />
        {t('group.info')}
      </Popover>
    </div>
  )

  const extra = (
    <div className={s.extra}>
      <Follow
        display={'both'}
        followeeGroupId={g.groupId}
        isFollowing={g.isFollowing}
        isPrivate={g.privateSetting === GroupPrivateSetting.Closed}
      />
    </div>
  )

  const creator = (
    <div className={s.creator}>
      <span style={{ fontSize: 12, marginRight: 4 }}>{t('group.creator')}</span>
      <Avatar style={{ marginRight: 4, width: 24, height: 24 }} src={g.creator.avatarUrl} />
      <UserHoverCard email={g.creator.email}>
        <Link to={routes.users.page(g.creator.email)}>{g.creator.name}</Link>
      </UserHoverCard>
    </div>
  )

  return (
    <div className={s.container}>
      {image}
      <div className={s.main}>
        {avatar}
        {title}
        {subtitle}
        {description}
        {extra}
      </div>
      {creator}
    </div>
  )
}

export const DiscoverGroupItem: React.FC<GroupCardProps> = ({ group: g }) => {
  return (
    <List.Item
      className={s.listItem}
      key={g.groupId}
      actions={[
        <span key={'xxx'}>
          <TeamOutlined style={{ marginRight: 4 }} />
          {g.followers!.totalCount}
          <FileTextOutlined style={{ marginRight: 4, marginLeft: 8 }} />
          {g.posts!.totalCount}
        </span>,
        <Follow
          key={'yyy'}
          display={'icon'}
          component={<div />}
          followeeGroupId={g.groupId}
          isFollowing={g.isFollowing}
          isPrivate={g.privateSetting === GroupPrivateSetting.Closed}
        />,
      ]}
    >
      <List.Item.Meta
        avatar={<GroupAvatar groupCatId={g.category?.groupCategoryId} />}
        title={<Link to={routes.groups.page(g.groupId)}>{g.name}</Link>}
        description={<div>{g.description}</div>}
      />
    </List.Item>
  )
}
